import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../utils/api';
import { toast } from 'sonner';
import {
  Building2, Users, CreditCard, Receipt, Clock, Plus,
  Search, Filter, Download, AlertCircle, CheckCircle,
  ChevronRight, Edit2, Trash2, Euro, Calendar, FileText,
  PieChart, ArrowUpRight, ArrowDownRight, RefreshCw, Settings,
  X, ChevronDown, Eye, Mail, Save, User
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';

// Normalize FastAPI/axios errors into a toast-safe string. Passing a raw 422
// detail array to toast.error crashes React (blank page), so always coerce.
const getApiErrorMessage = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => (typeof d === 'string' ? d : d?.msg)).filter(Boolean).join(', ') || fallback;
  }
  if (typeof detail === 'object' && detail.msg) return detail.msg;
  return fallback;
};

const FinanceModulePage = ({ onMenuClick }) => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [dashboard, setDashboard] = useState(null);
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [fundings, setFundings] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [students, setStudents] = useState([]);
  const [studentsFinancial, setStudentsFinancial] = useState([]);
  const [globalSchedule, setGlobalSchedule] = useState([]);
  const [reminderSettings, setReminderSettings] = useState(null);
  const [orgSettings, setOrgSettings] = useState(null);
  
  // Modal states
  const [showBeneficiaryModal, setShowBeneficiaryModal] = useState(false);
  const [showFundingModal, setShowFundingModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showStudentSheetModal, setShowStudentSheetModal] = useState(false);
  const [showOrgSettingsModal, setShowOrgSettingsModal] = useState(false);
  const [showAddScheduleItemModal, setShowAddScheduleItemModal] = useState(false);
  const [showGroupScheduleModal, setShowGroupScheduleModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentSheet, setStudentSheet] = useState(null);
  const [courses, setCourses] = useState([]);
  const [groups, setGroups] = useState([]);
  
  // Filter states
  const [scheduleFilters, setScheduleFilters] = useState({
    student_id: '', beneficiary_id: '', status: '', start_date: '', end_date: ''
  });
  const [scheduleSearch, setScheduleSearch] = useState('');
  
  // Form states
  const [beneficiaryForm, setBeneficiaryForm] = useState({
    name: '', beneficiary_type: 'opco', siret: '', address: '',
    postal_code: '', city: '', email: '', phone: '', contact_name: '', notes: ''
  });
  
  const [fundingForm, setFundingForm] = useState({
    student_id: '', course_id: '', total_amount: '', allocations: [],
    dossier_number: '', dossier_reference: '', training_start_date: '', training_end_date: ''
  });
  
  const [invoiceForm, setInvoiceForm] = useState({
    allocation_id: '', description: '', quantity: 1, unit: 'Formation',
    unit_price_ht: '', vat_rate: 0, due_date: '', additional_notes: '',
    invoice_type: 'standard', installment_number: ''
  });
  
  const [scheduleItemForm, setScheduleItemForm] = useState({
    allocation_id: '', due_date: '', amount: '', percentage: '', description: '', comment: ''
  });

  const [groupScheduleForm, setGroupScheduleForm] = useState({
    course_id: '', group_id: '', total_amount: '',
    installments: [{ due_date: '', percentage: 40, description: 'Versement 1 (40%)' }]
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const safe = (promise, fallback) => promise.catch(() => ({ data: fallback }));
      const [dashRes, beneRes, fundRes, invRes, studRes, studFinRes, setRes, orgRes, coursesRes, groupsRes] = await Promise.all([
        safe(api.get('/finance/dashboard'), null),
        safe(api.get('/finance/beneficiaries'), []),
        safe(api.get('/finance/student-fundings'), []),
        safe(api.get('/finance/invoices'), []),
        safe(api.get('/finance/students-with-courses'), []),
        safe(api.get('/finance/students-financial'), []),
        safe(api.get('/finance/reminder-settings'), null),
        safe(api.get('/finance/organization-settings'), null),
        safe(api.get('/content/courses'), []),
        safe(api.get('/groups/'), [])
      ]);

      setDashboard(dashRes.data);
      setBeneficiaries(beneRes.data);
      setFundings(fundRes.data?.fundings || fundRes.data || []);
      setInvoices(invRes.data);
      setStudents(studRes.data);
      setStudentsFinancial(studFinRes.data);
      setReminderSettings(setRes.data);
      setOrgSettings(orgRes.data);
      setCourses(coursesRes.data || []);
      setGroups(groupsRes.data || []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erreur lors du chargement des données');
    }
    setLoading(false);
  };

  const loadGlobalSchedule = async () => {
    try {
      const params = new URLSearchParams();
      Object.entries(scheduleFilters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      const response = await api.get(`/finance/global-schedule?${params.toString()}`);
      setGlobalSchedule(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des échéanciers');
    }
  };

  useEffect(() => {
    if (activeTab === 'schedules') {
      loadGlobalSchedule();
    }
  }, [activeTab, scheduleFilters]);

  const loadStudentSheet = async (studentId) => {
    try {
      const response = await api.get(`/finance/students/${studentId}/financial-sheet`);
      setStudentSheet(response.data);
      setShowStudentSheetModal(true);
    } catch (error) {
      toast.error('Erreur lors du chargement de la fiche');
    }
  };

  // Beneficiary handlers
  const handleCreateBeneficiary = async () => {
    if (!beneficiaryForm.name?.trim()) {
      toast.error('Veuillez renseigner le nom du bénéficiaire.');
      return;
    }
    if (!beneficiaryForm.beneficiary_type) {
      toast.error('Veuillez sélectionner le type de bénéficiaire.');
      return;
    }
    if (beneficiaryForm.email?.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(beneficiaryForm.email.trim())) {
      toast.error("L'adresse email du bénéficiaire n'est pas valide.");
      return;
    }
    try {
      await api.post('/finance/beneficiaries', beneficiaryForm);
      toast.success('Bénéficiaire créé');
      setShowBeneficiaryModal(false);
      setBeneficiaryForm({
        name: '', beneficiary_type: 'opco', siret: '', address: '',
        postal_code: '', city: '', email: '', phone: '', contact_name: '', notes: ''
      });
      loadData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Erreur lors de la création du bénéficiaire'));
    }
  };

  const handleDeleteBeneficiary = async (id) => {
    if (!window.confirm('Désactiver ce bénéficiaire ?')) return;
    try {
      await api.delete(`/finance/beneficiaries/${id}`);
      toast.success('Bénéficiaire désactivé');
      loadData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  // Funding handlers
  const handleCreateFunding = async () => {
    // Client-side validation avoids sending NaN (which triggers a 422 array the
    // toast can't render → blank page) and gives a clear reason instead.
    const totalAmount = parseFloat(fundingForm.total_amount);
    if (!fundingForm.student_id) {
      toast.error('Veuillez sélectionner un étudiant.');
      return;
    }
    if (!Number.isFinite(totalAmount) || totalAmount <= 0) {
      toast.error('Veuillez saisir un montant total valide.');
      return;
    }
    if (!fundingForm.allocations || fundingForm.allocations.length === 0) {
      toast.error('Veuillez ajouter au moins un financeur.');
      return;
    }
    for (const a of fundingForm.allocations) {
      if (!a.beneficiary_id) {
        toast.error('Chaque financeur doit être sélectionné.');
        return;
      }
      if (!Number.isFinite(parseFloat(a.amount)) || parseFloat(a.amount) <= 0) {
        toast.error('Chaque financeur doit avoir un montant valide.');
        return;
      }
    }

    try {
      const allocationsWithDates = fundingForm.allocations.map(a => ({
        ...a,
        schedule_dates: (a.schedule_dates || []).filter(d => d)
      }));
      const payload = {
        ...fundingForm,
        total_amount: totalAmount,
        student_id: parseInt(fundingForm.student_id),
        course_id: fundingForm.course_id ? parseInt(fundingForm.course_id) : null,
        allocations: allocationsWithDates.map(a => ({
          beneficiary_id: parseInt(a.beneficiary_id),
          amount: parseFloat(a.amount),
          percentage: a.percentage ? parseFloat(a.percentage) : null,
          schedule_dates: a.schedule_dates
        }))
      };

      await api.post('/finance/student-fundings', payload);
      toast.success('Financement créé');
      setShowFundingModal(false);
      setFundingForm({ student_id: '', course_id: '', total_amount: '', allocations: [],
        dossier_number: '', dossier_reference: '', training_start_date: '', training_end_date: '' });
      loadData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Erreur lors de la création du financement'));
    }
  };

  // Invoice handlers
  const handleCreateInvoice = async () => {
    // Client-side validation to avoid NaN -> null -> 422 (which used to blank the page)
    const allocationId = parseInt(invoiceForm.allocation_id, 10);
    const unitPrice = parseFloat(invoiceForm.unit_price_ht);
    if (!invoiceForm.allocation_id || Number.isNaN(allocationId)) {
      toast.error('Veuillez sélectionner une allocation de financement.');
      return;
    }
    if (!invoiceForm.description?.trim()) {
      toast.error('Veuillez saisir une description.');
      return;
    }
    if (Number.isNaN(unitPrice) || unitPrice <= 0) {
      toast.error('Veuillez saisir un prix HT valide.');
      return;
    }
    if (!invoiceForm.due_date) {
      toast.error("Veuillez saisir une date d'échéance.");
      return;
    }

    try {
      const quantity = parseFloat(invoiceForm.quantity);
      const vatRate = parseFloat(invoiceForm.vat_rate);
      const payload = {
        ...invoiceForm,
        allocation_id: allocationId,
        quantity: Number.isNaN(quantity) ? 1 : quantity,
        unit_price_ht: unitPrice,
        vat_rate: Number.isNaN(vatRate) ? 0 : vatRate,
        installment_number: invoiceForm.installment_number ? parseInt(invoiceForm.installment_number, 10) : null
      };
      
      await api.post('/finance/invoices', payload);
      toast.success('Facture créée');
      setShowInvoiceModal(false);
      setInvoiceForm({
        allocation_id: '', description: '', quantity: 1, unit: 'Formation',
        unit_price_ht: '', vat_rate: 0, due_date: '', additional_notes: '',
        invoice_type: 'standard', installment_number: ''
      });
      loadData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Erreur lors de la création de la facture'));
    }
  };

  const handleDownloadInvoice = async (invoiceId) => {
    try {
      const response = await api.get(`/finance/invoices/${invoiceId}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `facture_${invoiceId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const handleSendInvoiceEmail = async (invoiceId, email) => {
    const recipientEmail = email || prompt('Email du destinataire:');
    if (!recipientEmail) return;
    
    try {
      await api.post(`/finance/invoices/${invoiceId}/send-email`, null, {
        params: { recipient_email: recipientEmail }
      });
      toast.success('Facture envoyée par email');
      loadData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Erreur d'envoi"));
    }
  };

  const handleRecordPayment = async (invoiceId, amount) => {
    const paidAmount = prompt('Montant payé (€):', amount);
    if (!paidAmount) return;
    
    try {
      await api.put(`/finance/invoices/${invoiceId}/payment`, {
        paid_amount: parseFloat(paidAmount),
        paid_date: new Date().toISOString().split('T')[0]
      });
      toast.success('Paiement enregistré');
      loadData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  // Schedule item handlers
  const handleAddScheduleItem = async () => {
    if (!studentSheet || !scheduleItemForm.allocation_id) return;
    
    try {
      await api.post(`/finance/students/${studentSheet.student.id}/schedule-items`, scheduleItemForm, {
        params: { allocation_id: scheduleItemForm.allocation_id }
      });
      toast.success('Échéance ajoutée');
      setShowAddScheduleItemModal(false);
      setScheduleItemForm({ allocation_id: '', due_date: '', amount: '', percentage: '', description: '', comment: '' });
      loadStudentSheet(studentSheet.student.id);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Erreur lors de l'ajout de l'échéance"));
    }
  };

  const handleUpdateScheduleItem = async (itemId, data) => {
    try {
      await api.put(`/finance/schedule-items/${itemId}`, data);
      toast.success('Échéance mise à jour');
      if (studentSheet) {
        loadStudentSheet(studentSheet.student.id);
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDeleteScheduleItem = async (itemId) => {
    if (!window.confirm('Supprimer cette échéance ?')) return;
    try {
      await api.delete(`/finance/schedule-items/${itemId}`);
      toast.success('Échéance supprimée');
      if (studentSheet) {
        loadStudentSheet(studentSheet.student.id);
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  // Organization settings handlers
  const handleUpdateOrgSettings = async () => {
    try {
      await api.put('/finance/organization-settings', orgSettings);
      toast.success('Paramètres du centre mis à jour');
      setShowOrgSettingsModal(false);
      loadData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  // Group schedule handlers
  const handleCreateGroupSchedule = async () => {
    // Required fields
    if (!groupScheduleForm.course_id && !groupScheduleForm.group_id) {
      toast.error('Veuillez sélectionner un cours ou un groupe.');
      return;
    }
    const totalAmount = parseFloat(groupScheduleForm.total_amount);
    if (Number.isNaN(totalAmount) || totalAmount <= 0) {
      toast.error('Veuillez saisir un montant total valide.');
      return;
    }
    if (!groupScheduleForm.installments.length) {
      toast.error('Veuillez ajouter au moins une échéance.');
      return;
    }
    if (groupScheduleForm.installments.some((inst) => !inst.due_date)) {
      toast.error('Chaque échéance doit avoir une date.');
      return;
    }
    // Validate percentages
    const totalPct = groupScheduleForm.installments.reduce((sum, inst) => sum + (parseFloat(inst.percentage) || 0), 0);
    if (Math.abs(totalPct - 100) > 0.1) {
      toast.error(`Les pourcentages doivent totaliser 100% (actuellement ${totalPct.toFixed(1)}%)`);
      return;
    }
    
    try {
      const payload = {
        course_id: groupScheduleForm.course_id ? parseInt(groupScheduleForm.course_id) : null,
        group_id: groupScheduleForm.group_id ? parseInt(groupScheduleForm.group_id) : null,
        total_amount: totalAmount,
        installments: groupScheduleForm.installments.map(inst => ({
          due_date: inst.due_date,
          percentage: parseFloat(inst.percentage),
          description: inst.description
        })),
        apply_to_students: true
      };
      
      const response = await api.post('/finance/group-schedules', payload);
      toast.success(response.data.message);
      setShowGroupScheduleModal(false);
      setGroupScheduleForm({
        course_id: '', group_id: '', total_amount: '',
        installments: [{ due_date: '', percentage: 40, description: 'Versement 1 (40%)' }]
      });
      loadData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Erreur lors de la création de l'échéancier"));
    }
  };

  const addGroupInstallment = () => {
    const currentTotal = groupScheduleForm.installments.reduce((sum, inst) => sum + (parseFloat(inst.percentage) || 0), 0);
    const remaining = 100 - currentTotal;
    const num = groupScheduleForm.installments.length + 1;
    
    setGroupScheduleForm({
      ...groupScheduleForm,
      installments: [...groupScheduleForm.installments, {
        due_date: '',
        percentage: remaining > 0 ? remaining : 0,
        description: `Versement ${num} (${remaining > 0 ? remaining : 0}%)`
      }]
    });
  };

  const updateGroupInstallment = (index, field, value) => {
    const newInstallments = [...groupScheduleForm.installments];
    newInstallments[index][field] = value;
    
    if (field === 'percentage') {
      newInstallments[index].description = `Versement ${index + 1} (${value}%)`;
    }
    
    setGroupScheduleForm({ ...groupScheduleForm, installments: newInstallments });
  };

  const removeGroupInstallment = (index) => {
    setGroupScheduleForm({
      ...groupScheduleForm,
      installments: groupScheduleForm.installments.filter((_, i) => i !== index)
    });
  };

  // Reminder handlers
  const handleUpdateSettings = async () => {
    try {
      await api.put('/finance/reminder-settings', reminderSettings);
      toast.success('Paramètres mis à jour');
      setShowSettingsModal(false);
    } catch (error) {
      toast.error('Erreur');
    }
  };

  // Export handlers
  const handleExport = async (type, format = 'xlsx') => {
    try {
      const endpoint = type === 'schedules' ? '/finance/export/schedules' : '/finance/export/invoices';
      const response = await api.get(`${endpoint}?format=${format}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}_export.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Export téléchargé');
    } catch (error) {
      toast.error('Erreur lors de l\'export');
    }
  };

  const addAllocation = () => {
    setFundingForm({
      ...fundingForm,
      allocations: [...fundingForm.allocations, { beneficiary_id: '', amount: '', percentage: '', schedule_dates: [''] }]
    });
  };

  const updateAllocation = (index, field, value) => {
    const newAllocations = [...fundingForm.allocations];
    newAllocations[index][field] = value;
    
    if (field === 'amount' && fundingForm.total_amount) {
      newAllocations[index].percentage = ((parseFloat(value) / parseFloat(fundingForm.total_amount)) * 100).toFixed(1);
    }
    if (field === 'percentage' && fundingForm.total_amount) {
      newAllocations[index].amount = ((parseFloat(value) / 100) * parseFloat(fundingForm.total_amount)).toFixed(2);
    }
    
    setFundingForm({ ...fundingForm, allocations: newAllocations });
  };

  const removeAllocation = (index) => {
    setFundingForm({
      ...fundingForm,
      allocations: fundingForm.allocations.filter((_, i) => i !== index)
    });
  };

  const getBeneficiaryTypeBadge = (type) => {
    const styles = {
      opco: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
      company: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
      student: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      other: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400'
    };
    const labels = { opco: 'OPCO', company: 'Entreprise', student: 'Étudiant', other: 'Autre' };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[type] || styles.other}`}>
        {labels[type] || type}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
      partial: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
      paid: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      overdue: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
      no_funding: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400'
    };
    const labels = { pending: 'En attente', partial: 'Partiel', paid: 'Payé', overdue: 'En retard', no_funding: 'Non configuré' };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
        {labels[status] || status}
      </span>
    );
  };

  const tabs = [
    { id: 'dashboard', label: 'Tableau de bord', icon: PieChart },
    { id: 'students', label: 'Étudiants', icon: Users },
    { id: 'beneficiaries', label: 'Bénéficiaires', icon: Building2 },
    { id: 'fundings', label: 'Financements', icon: Euro },
    { id: 'schedules', label: 'Échéanciers', icon: Calendar },
    { id: 'invoices', label: 'Factures', icon: Receipt },
    { id: 'settings', label: 'Paramètres', icon: Settings }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      {/* Header */}
      <header className="glass-header sticky top-0 z-30 px-4 sm:px-6 lg:px-8 py-4 border-b border-slate-200 dark:border-slate-700">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-indigo-950 dark:text-white font-outfit">
              Module Financier
            </h1>
            <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
              Gestion des financements, factures et paiements
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => handleExport('invoices', 'xlsx')}
              variant="outline"
              className="hidden sm:flex"
              data-testid="export-invoices-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Export Excel
            </Button>
            <Button onClick={loadData} variant="ghost" size="icon">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-1 overflow-x-auto py-2" data-testid="finance-tabs">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  data-testid={`tab-${tab.id}`}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400'
                      : 'text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-700'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && dashboard && (
          <div className="space-y-6" data-testid="dashboard-section">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white dark:bg-slate-800 rounded-xl p-6 border border-slate-200 dark:border-slate-700">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600 dark:text-slate-400">Total facturé</p>
                    <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">
                      {dashboard.summary.total_invoiced.toLocaleString('fr-FR')} €
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                    <Receipt className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  </div>
                </div>
              </div>
              
              <div className="bg-white dark:bg-slate-800 rounded-xl p-6 border border-slate-200 dark:border-slate-700">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600 dark:text-slate-400">Total encaissé</p>
                    <p className="text-2xl font-bold text-green-600 dark:text-green-400 mt-1">
                      {dashboard.summary.total_paid.toLocaleString('fr-FR')} €
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                    <ArrowUpRight className="w-6 h-6 text-green-600 dark:text-green-400" />
                  </div>
                </div>
              </div>
              
              <div className="bg-white dark:bg-slate-800 rounded-xl p-6 border border-slate-200 dark:border-slate-700">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600 dark:text-slate-400">Reste à encaisser</p>
                    <p className="text-2xl font-bold text-orange-600 dark:text-orange-400 mt-1">
                      {dashboard.summary.outstanding.toLocaleString('fr-FR')} €
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center">
                    <Clock className="w-6 h-6 text-orange-600 dark:text-orange-400" />
                  </div>
                </div>
              </div>
              
              <div className="bg-white dark:bg-slate-800 rounded-xl p-6 border border-slate-200 dark:border-slate-700">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600 dark:text-slate-400">Factures en retard</p>
                    <p className="text-2xl font-bold text-red-600 dark:text-red-400 mt-1">
                      {dashboard.summary.overdue_invoices}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center">
                    <AlertCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
                  </div>
                </div>
              </div>
            </div>

            {/* Monthly Payment Forecast - NEW */}
            {dashboard.monthly_payment_forecast && dashboard.monthly_payment_forecast.length > 0 && (
              <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
                <div className="p-4 border-b border-slate-200 dark:border-slate-700">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-violet-600" />
                      Prévisions de paiements par mois
                    </h3>
                    <span className="text-sm text-slate-500">
                      Total à recevoir: {dashboard.monthly_payment_forecast.reduce((sum, m) => sum + m.expected_amount, 0).toLocaleString('fr-FR')} €
                    </span>
                  </div>
                </div>
                <div className="p-4">
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                    {dashboard.monthly_payment_forecast.slice(0, 6).map((month) => (
                      <div 
                        key={month.month} 
                        className="bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-700/50 dark:to-slate-700 rounded-lg p-4 text-center border border-slate-200 dark:border-slate-600"
                      >
                        <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                          {month.month_label}
                        </p>
                        <p className="text-xl font-bold text-violet-600 dark:text-violet-400 mt-1">
                          {month.expected_amount.toLocaleString('fr-FR')} €
                        </p>
                        {month.from_schedules > 0 && month.from_invoices > 0 && (
                          <div className="text-xs text-slate-500 mt-1 space-y-0.5">
                            <p>Échéanciers: {month.from_schedules.toLocaleString('fr-FR')} €</p>
                            <p>Factures: {month.from_invoices.toLocaleString('fr-FR')} €</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  {dashboard.monthly_payment_forecast.length > 6 && (
                    <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                      <details className="group">
                        <summary className="cursor-pointer text-sm text-violet-600 hover:text-violet-700 font-medium flex items-center gap-1">
                          <ChevronDown className="w-4 h-4 transition-transform group-open:rotate-180" />
                          Voir les {dashboard.monthly_payment_forecast.length - 6} mois suivants
                        </summary>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mt-3">
                          {dashboard.monthly_payment_forecast.slice(6).map((month) => (
                            <div 
                              key={month.month} 
                              className="bg-slate-50 dark:bg-slate-700/30 rounded-lg p-3 text-center border border-slate-200 dark:border-slate-600"
                            >
                              <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
                                {month.month_label}
                              </p>
                              <p className="text-lg font-bold text-slate-700 dark:text-slate-300 mt-1">
                                {month.expected_amount.toLocaleString('fr-FR')} €
                              </p>
                            </div>
                          ))}
                        </div>
                      </details>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Recent & Upcoming */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
                <div className="p-4 border-b border-slate-200 dark:border-slate-700">
                  <h3 className="font-semibold text-slate-900 dark:text-white">Dernières factures</h3>
                </div>
                <div className="divide-y divide-slate-200 dark:divide-slate-700">
                  {dashboard.recent_invoices.length === 0 ? (
                    <div className="p-8 text-center text-slate-500">Aucune facture</div>
                  ) : (
                    dashboard.recent_invoices.map((inv) => (
                      <div key={inv.id} className="p-4 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-700/50">
                        <div>
                          <p className="font-medium text-slate-900 dark:text-white">{inv.invoice_number}</p>
                          <p className="text-sm text-slate-600 dark:text-slate-400">{inv.recipient_name}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-medium text-slate-900 dark:text-white">{inv.total_ttc?.toLocaleString('fr-FR')} €</p>
                          {getStatusBadge(inv.payment_status)}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
                <div className="p-4 border-b border-slate-200 dark:border-slate-700">
                  <h3 className="font-semibold text-slate-900 dark:text-white">Répartition par type</h3>
                </div>
                <div className="p-4 space-y-4">
                  {dashboard.beneficiary_breakdown.length === 0 ? (
                    <div className="text-center text-slate-500 py-8">Aucune donnée</div>
                  ) : (
                    dashboard.beneficiary_breakdown.map((stat, idx) => (
                      <div key={idx} className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {getBeneficiaryTypeBadge(stat.type)}
                          <span className="text-slate-600 dark:text-slate-400">{stat.count} bénéficiaire(s)</span>
                        </div>
                        <span className="font-medium text-slate-900 dark:text-white">
                          {stat.total_amount.toLocaleString('fr-FR')} €
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Students Tab - NEW */}
        {activeTab === 'students' && (
          <div className="space-y-4" data-testid="students-section">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                Étudiants - Suivi financier ({studentsFinancial.length})
              </h2>
              <Button onClick={() => handleExport('schedules', 'xlsx')} variant="outline">
                <Download className="w-4 h-4 mr-2" />
                Export Excel
              </Button>
            </div>
            
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 dark:bg-slate-700/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Étudiant</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Formation</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Source financement</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Total (€)</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Reçu (€)</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Reste (€)</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Prochaine échéance</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Statut</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                    {studentsFinancial.map((s, index) => (
                      <tr key={`${s.id}-${s.funding_id || index}`} className="hover:bg-slate-50 dark:hover:bg-slate-700/50">
                        <td className="px-4 py-3">
                          <p className="font-medium text-slate-900 dark:text-white">{s.student_name}</p>
                          <p className="text-xs text-slate-500">{s.student_number}</p>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-900 dark:text-white">{s.training_name}</td>
                        <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">{s.funding_source}</td>
                        <td className="px-4 py-3 text-right font-medium text-slate-900 dark:text-white">{s.total_amount?.toLocaleString('fr-FR')}</td>
                        <td className="px-4 py-3 text-right text-green-600 dark:text-green-400">{s.amount_received?.toLocaleString('fr-FR')}</td>
                        <td className="px-4 py-3 text-right text-orange-600 dark:text-orange-400">{s.remaining_balance?.toLocaleString('fr-FR')}</td>
                        <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">{s.next_payment_date || '-'}</td>
                        <td className="px-4 py-3 text-center">{getStatusBadge(s.payment_status)}</td>
                        <td className="px-4 py-3 text-right">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => loadStudentSheet(s.id)}
                            data-testid={`view-student-sheet-${s.id}`}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            Fiche financière
                          </Button>
                        </td>
                      </tr>
                    ))}
                    {studentsFinancial.length === 0 && (
                      <tr>
                        <td colSpan={9} className="px-4 py-12 text-center text-slate-500">
                          Aucun étudiant avec données financières
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Beneficiaries Tab */}
        {activeTab === 'beneficiaries' && (
          <div className="space-y-4" data-testid="beneficiaries-section">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                Bénéficiaires ({beneficiaries.length})
              </h2>
              <Button onClick={() => setShowBeneficiaryModal(true)} data-testid="add-beneficiary-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nouveau bénéficiaire
              </Button>
            </div>
            
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-50 dark:bg-slate-700/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Nom</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Contact</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">SIRET</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                  {beneficiaries.map((b) => (
                    <tr key={b.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/50">
                      <td className="px-4 py-3">
                        <p className="font-medium text-slate-900 dark:text-white">{b.name}</p>
                        <p className="text-sm text-slate-500">{b.city}</p>
                      </td>
                      <td className="px-4 py-3">{getBeneficiaryTypeBadge(b.beneficiary_type)}</td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-slate-900 dark:text-white">{b.email}</p>
                        <p className="text-sm text-slate-500">{b.phone}</p>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">{b.siret || '-'}</td>
                      <td className="px-4 py-3 text-right">
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteBeneficiary(b.id)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {beneficiaries.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-4 py-12 text-center text-slate-500">
                        Aucun bénéficiaire
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Fundings Tab */}
        {activeTab === 'fundings' && (
          <div className="space-y-4" data-testid="fundings-section">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                Financements étudiants
              </h2>
              <Button onClick={() => setShowFundingModal(true)} data-testid="add-funding-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nouveau financement
              </Button>
            </div>
            
            <div className="space-y-4">
              {Array.isArray(fundings) && fundings.length === 0 ? (
                <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-12 text-center">
                  <Users className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                  <p className="text-slate-600 dark:text-slate-400">Aucun financement configuré</p>
                </div>
              ) : (
                (Array.isArray(fundings) ? fundings : []).map((f) => (
                  <div key={f.id} className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="font-semibold text-slate-900 dark:text-white">{f.student_name || 'Étudiant'}</h3>
                        <p className="text-sm text-slate-600 dark:text-slate-400">
                          Montant total: {f.total_amount?.toLocaleString('fr-FR')} €
                          {f.dossier_number && <span className="ml-3">N° Dossier: {f.dossier_number}</span>}
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setInvoiceForm({ ...invoiceForm, allocation_id: f.allocations?.[0]?.id || '' });
                          setShowInvoiceModal(true);
                        }}
                      >
                        <Receipt className="w-4 h-4 mr-2" />
                        Créer facture
                      </Button>
                    </div>
                    
                    <div className="space-y-2">
                      {f.allocations?.map((a, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                          <div className="flex items-center gap-3">
                            {getBeneficiaryTypeBadge(a.beneficiary_type)}
                            <span className="font-medium text-slate-900 dark:text-white">{a.beneficiary_name}</span>
                            <span className="text-sm text-slate-500">({a.percentage?.toFixed(0)}%)</span>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <p className="font-medium text-slate-900 dark:text-white">{a.amount?.toLocaleString('fr-FR')} €</p>
                            </div>
                            {getStatusBadge(a.payment_status)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Global Schedules Tab - NEW */}
        {activeTab === 'schedules' && (() => {
          const q = scheduleSearch.toLowerCase();
          const filteredSchedule = q
            ? globalSchedule.filter(item =>
                (item.student_name || '').toLowerCase().includes(q) ||
                (item.beneficiary_name || '').toLowerCase().includes(q) ||
                (item.beneficiary_type || '').toLowerCase().includes(q) ||
                (item.course_name || '').toLowerCase().includes(q) ||
                (item.description || '').toLowerCase().includes(q)
              )
            : globalSchedule;
          return (
          <div className="space-y-4" data-testid="schedules-section">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                Échéancier global
              </h2>
              <div className="flex gap-2">
                <Button onClick={() => setShowGroupScheduleModal(true)} variant="outline" data-testid="create-group-schedule-btn">
                  <Users className="w-4 h-4 mr-2" />
                  Créer échéancier groupe
                </Button>
                <Button onClick={() => handleExport('schedules', 'xlsx')} variant="outline">
                  <Download className="w-4 h-4 mr-2" />
                  Export Excel
                </Button>
              </div>
            </div>

            {/* Search bar */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={scheduleSearch}
                onChange={(e) => setScheduleSearch(e.target.value)}
                placeholder="Rechercher par nom, prénom, e-mail, OPCO, financeur..."
                className="w-full pl-10 pr-4 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm"
              />
            </div>

            {/* Filters */}
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Étudiant</label>
                  <select
                    value={scheduleFilters.student_id}
                    onChange={(e) => setScheduleFilters({ ...scheduleFilters, student_id: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm"
                  >
                    <option value="">Tous</option>
                    {students.map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Bénéficiaire</label>
                  <select
                    value={scheduleFilters.beneficiary_id}
                    onChange={(e) => setScheduleFilters({ ...scheduleFilters, beneficiary_id: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm"
                  >
                    <option value="">Tous</option>
                    {beneficiaries.map((b) => (
                      <option key={b.id} value={b.id}>{b.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Statut</label>
                  <select
                    value={scheduleFilters.status}
                    onChange={(e) => setScheduleFilters({ ...scheduleFilters, status: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm"
                  >
                    <option value="">Tous</option>
                    <option value="pending">En attente</option>
                    <option value="paid">Payé</option>
                    <option value="overdue">En retard</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Date début</label>
                  <input
                    type="date"
                    value={scheduleFilters.start_date}
                    onChange={(e) => setScheduleFilters({ ...scheduleFilters, start_date: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Date fin</label>
                  <input
                    type="date"
                    value={scheduleFilters.end_date}
                    onChange={(e) => setScheduleFilters({ ...scheduleFilters, end_date: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm"
                  />
                </div>
              </div>
            </div>
            
            {/* Schedule Table */}
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 dark:bg-slate-700/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Date échéance</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Étudiant</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Bénéficiaire</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Description</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Montant (€)</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Statut</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                    {filteredSchedule.map((item) => (
                      <tr key={item.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/50">
                        <td className="px-4 py-3 font-medium text-slate-900 dark:text-white">{item.due_date}</td>
                        <td className="px-4 py-3 text-sm text-slate-900 dark:text-white">{item.student_name}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            {getBeneficiaryTypeBadge(item.beneficiary_type)}
                            <span className="text-sm">{item.beneficiary_name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">{item.description || '-'}</td>
                        <td className="px-4 py-3 text-right font-medium text-slate-900 dark:text-white">{item.amount?.toLocaleString('fr-FR')}</td>
                        <td className="px-4 py-3 text-center">{getStatusBadge(item.status)}</td>
                      </tr>
                    ))}
                    {filteredSchedule.length === 0 && (
                      <tr>
                        <td colSpan={6} className="px-4 py-12 text-center text-slate-500">
                          Aucune échéance trouvée
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          );
        })()}

        {/* Invoices Tab */}
        {activeTab === 'invoices' && (
          <div className="space-y-4" data-testid="invoices-section">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                Factures ({invoices.length})
              </h2>
              <div className="flex gap-2">
                <Button onClick={() => handleExport('invoices', 'xlsx')} variant="outline">
                  <Download className="w-4 h-4 mr-2" />
                  Export
                </Button>
                <Button onClick={() => setShowInvoiceModal(true)} data-testid="add-invoice-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Créer une facture
                </Button>
              </div>
            </div>
            
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-50 dark:bg-slate-700/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">N° Facture</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Destinataire</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Échéance</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Montant TTC</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Statut</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                  {invoices.map((inv) => (
                    <tr key={inv.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/50">
                      <td className="px-4 py-3">
                        <p className="font-medium text-slate-900 dark:text-white">{inv.invoice_number}</p>
                        <p className="text-xs text-slate-500">{inv.issue_date}</p>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-900 dark:text-white">{inv.recipient_name}</td>
                      <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">{inv.due_date}</td>
                      <td className="px-4 py-3 text-right font-medium text-slate-900 dark:text-white">
                        {inv.total_ttc?.toLocaleString('fr-FR')} €
                      </td>
                      <td className="px-4 py-3 text-center">{getStatusBadge(inv.payment_status)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleDownloadInvoice(inv.id)} title="Télécharger PDF">
                            <Download className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleSendInvoiceEmail(inv.id)} title="Envoyer par email">
                            <Mail className="w-4 h-4" />
                          </Button>
                          {inv.payment_status !== 'paid' && (
                            <Button variant="ghost" size="sm" onClick={() => handleRecordPayment(inv.id, inv.total_ttc)} title="Enregistrer paiement">
                              <CreditCard className="w-4 h-4 text-green-600" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {invoices.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-4 py-12 text-center text-slate-500">
                        Aucune facture
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="space-y-6" data-testid="settings-section">
            {/* Organization Settings */}
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                  Coordonnées bancaires
                </h3>
                <Button onClick={() => setShowOrgSettingsModal(true)}>
                  <Edit2 className="w-4 h-4 mr-2" />
                  Modifier
                </Button>
              </div>
              
              {orgSettings && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-500">Nom</p>
                    <p className="font-medium text-slate-900 dark:text-white">{orgSettings.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">SIRET</p>
                    <p className="font-medium text-slate-900 dark:text-white">{orgSettings.siret || 'Non défini'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">NDA</p>
                    <p className="font-medium text-slate-900 dark:text-white">{orgSettings.nda || 'Non défini'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Email</p>
                    <p className="font-medium text-slate-900 dark:text-white">{orgSettings.email || 'Non défini'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Téléphone</p>
                    <p className="font-medium text-slate-900 dark:text-white">{orgSettings.phone || 'Non défini'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">IBAN</p>
                    <p className="font-medium text-slate-900 dark:text-white">{orgSettings.iban || 'Non défini'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Préfixe factures</p>
                    <p className="font-medium text-slate-900 dark:text-white">{orgSettings.invoice_prefix || 'FAC'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Prochain numéro</p>
                    <p className="font-medium text-slate-900 dark:text-white">{orgSettings.invoice_next_number || 1}</p>
                  </div>
                </div>
              )}
            </div>
            
            {/* Reminder Settings */}
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
                Paramètres des rappels automatiques
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">
                Configurez quand envoyer des rappels de paiement automatiques (J-7, J-1, J, J+7, etc.)
              </p>
              
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {[
                  { key: 'reminder_before_7d', label: 'J-7 (7 jours avant)' },
                  { key: 'reminder_before_1d', label: 'J-1 (1 jour avant)' },
                  { key: 'reminder_on_due', label: "J (jour de l'échéance)" },
                  { key: 'reminder_after_7d', label: 'J+7 (7 jours après)' },
                  { key: 'reminder_after_14d', label: 'J+14 (14 jours après)' },
                  { key: 'reminder_after_30d', label: 'J+30 (30 jours après)' },
                ].map((item) => (
                  <label key={item.key} className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg cursor-pointer">
                    <input
                      type="checkbox"
                      checked={reminderSettings?.[item.key] ?? false}
                      onChange={(e) => setReminderSettings({ ...reminderSettings, [item.key]: e.target.checked })}
                      className="w-4 h-4 text-violet-600 rounded"
                    />
                    <span className="text-sm text-slate-700 dark:text-slate-300">{item.label}</span>
                  </label>
                ))}
              </div>
              
              <div className="mt-6 flex justify-end">
                <Button onClick={handleUpdateSettings}>
                  Enregistrer les paramètres
                </Button>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Student Financial Sheet Modal */}
      <Dialog open={showStudentSheetModal} onOpenChange={setShowStudentSheetModal}>
        <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Fiche financière - {studentSheet?.student?.name}</DialogTitle>
          </DialogHeader>
          
          {studentSheet && (
            <div className="space-y-6 py-4">
              {/* Student Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                <div>
                  <p className="text-sm text-slate-500">Étudiant</p>
                  <p className="font-medium">{studentSheet.student.name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Formation</p>
                  <p className="font-medium">{studentSheet.training.name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">N° Dossier</p>
                  <p className="font-medium">{studentSheet.funding.dossier_number || 'Non défini'}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Montant total</p>
                  <p className="font-medium">{studentSheet.funding.total_amount?.toLocaleString('fr-FR')} €</p>
                </div>
              </div>
              
              {/* Summary */}
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-center">
                  <p className="text-2xl font-bold text-blue-600">{studentSheet.summary.total_amount?.toLocaleString('fr-FR')} €</p>
                  <p className="text-sm text-slate-600">Total</p>
                </div>
                <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg text-center">
                  <p className="text-2xl font-bold text-green-600">{studentSheet.summary.total_paid?.toLocaleString('fr-FR')} €</p>
                  <p className="text-sm text-slate-600">Payé</p>
                </div>
                <div className="p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg text-center">
                  <p className="text-2xl font-bold text-orange-600">{studentSheet.summary.remaining?.toLocaleString('fr-FR')} €</p>
                  <p className="text-sm text-slate-600">Reste</p>
                </div>
              </div>
              
              {/* Allocations */}
              <div>
                <h4 className="font-semibold mb-2">Sources de financement</h4>
                <div className="space-y-2">
                  {studentSheet.allocations.map((a) => (
                    <div key={a.id} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                      <div className="flex items-center gap-2">
                        {getBeneficiaryTypeBadge(a.beneficiary_type)}
                        <span>{a.beneficiary_name}</span>
                        <span className="text-slate-500">({a.percentage?.toFixed(0)}%)</span>
                      </div>
                      <span className="font-medium">{a.amount?.toLocaleString('fr-FR')} €</span>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Payment Schedule */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold">Échéancier de paiement</h4>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setScheduleItemForm({
                        allocation_id: studentSheet.allocations[0]?.id || '',
                        due_date: '', amount: '', percentage: '', description: '', comment: ''
                      });
                      setShowAddScheduleItemModal(true);
                    }}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Ajouter échéance
                  </Button>
                </div>
                
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 dark:bg-slate-700/50">
                      <tr>
                        <th className="px-3 py-2 text-left">Date</th>
                        <th className="px-3 py-2 text-left">Bénéficiaire</th>
                        <th className="px-3 py-2 text-left">Description</th>
                        <th className="px-3 py-2 text-right">Montant</th>
                        <th className="px-3 py-2 text-center">Statut</th>
                        <th className="px-3 py-2 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {studentSheet.schedule_items.map((item) => (
                        <tr key={item.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/50">
                          <td className="px-3 py-2 font-medium">{item.due_date}</td>
                          <td className="px-3 py-2">{item.beneficiary_name}</td>
                          <td className="px-3 py-2 text-slate-600">{item.description || '-'}</td>
                          <td className="px-3 py-2 text-right">{item.amount?.toLocaleString('fr-FR')} €</td>
                          <td className="px-3 py-2 text-center">{getStatusBadge(item.status)}</td>
                          <td className="px-3 py-2 text-right">
                            <div className="flex justify-end gap-1">
                              {item.status !== 'paid' && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleUpdateScheduleItem(item.id, {
                                    payment_status: 'paid',
                                    paid_amount: item.amount,
                                    paid_date: new Date().toISOString().split('T')[0]
                                  })}
                                  title="Marquer payé"
                                >
                                  <CheckCircle className="w-4 h-4 text-green-600" />
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteScheduleItem(item.id)}
                                title="Supprimer"
                              >
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {studentSheet.schedule_items.length === 0 && (
                        <tr>
                          <td colSpan={6} className="px-3 py-8 text-center text-slate-500">
                            Aucune échéance. Cliquez sur "Ajouter échéance" pour créer un échéancier.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              
              {/* Invoices */}
              {studentSheet.invoices.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2">Factures</h4>
                  <div className="space-y-2">
                    {studentSheet.invoices.map((inv) => (
                      <div key={inv.id} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                        <div>
                          <span className="font-medium">{inv.invoice_number}</span>
                          <span className="text-slate-500 ml-2">- {inv.beneficiary_name}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span>{inv.total_ttc?.toLocaleString('fr-FR')} €</span>
                          {getStatusBadge(inv.payment_status)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Schedule Item Modal */}
      <Dialog open={showAddScheduleItemModal} onOpenChange={setShowAddScheduleItemModal}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Ajouter une échéance</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium mb-1">Bénéficiaire *</label>
              <select
                value={scheduleItemForm.allocation_id}
                onChange={(e) => setScheduleItemForm({ ...scheduleItemForm, allocation_id: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder:text-slate-400"
              >
                <option value="">Sélectionner...</option>
                {studentSheet?.allocations?.map((a) => (
                  <option key={a.id} value={a.id}>{a.beneficiary_name} ({a.amount?.toLocaleString('fr-FR')} €)</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Date d'échéance *</label>
              <input
                type="date"
                value={scheduleItemForm.due_date}
                onChange={(e) => setScheduleItemForm({ ...scheduleItemForm, due_date: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder:text-slate-400"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Montant (€) *</label>
                <input
                  type="number"
                  value={scheduleItemForm.amount}
                  onChange={(e) => setScheduleItemForm({ ...scheduleItemForm, amount: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder:text-slate-400"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">% (optionnel)</label>
                <input
                  type="number"
                  value={scheduleItemForm.percentage}
                  onChange={(e) => setScheduleItemForm({ ...scheduleItemForm, percentage: e.target.value })}
                  placeholder="ex: 40"
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder:text-slate-400"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Description</label>
              <input
                type="text"
                value={scheduleItemForm.description}
                onChange={(e) => setScheduleItemForm({ ...scheduleItemForm, description: e.target.value })}
                placeholder="ex: Versement 1"
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder:text-slate-400"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Commentaire</label>
              <textarea
                value={scheduleItemForm.comment}
                onChange={(e) => setScheduleItemForm({ ...scheduleItemForm, comment: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder:text-slate-400"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddScheduleItemModal(false)}>Annuler</Button>
            <Button onClick={handleAddScheduleItem}>Ajouter</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Organization Settings Modal */}
      <Dialog open={showOrgSettingsModal} onOpenChange={setShowOrgSettingsModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Paramètres du centre</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div>
              <label className="block text-sm font-medium mb-1">Nom du centre *</label>
              <input
                type="text"
                value={orgSettings?.name || ''}
                onChange={(e) => setOrgSettings({ ...orgSettings, name: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder:text-slate-400"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">SIRET</label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={14}
                  value={orgSettings?.siret || ''}
                  onChange={(e) => setOrgSettings({ ...orgSettings, siret: e.target.value.replace(/\D/g, '').slice(0, 14) })}
                  placeholder="14 chiffres"
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder:text-slate-400"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">NDA</label>
                <input
                  type="text"
                  maxLength={14}
                  value={orgSettings?.nda || ''}
                  onChange={(e) => setOrgSettings({ ...orgSettings, nda: e.target.value.slice(0, 14) })}
                  placeholder="11999999999"
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder:text-slate-400"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Adresse</label>
              <input
                type="text"
                value={orgSettings?.address || ''}
                onChange={(e) => setOrgSettings({ ...orgSettings, address: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder:text-slate-400"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Code postal</label>
                <input
                  type="text"
                  value={orgSettings?.postal_code || ''}
                  onChange={(e) => setOrgSettings({ ...orgSettings, postal_code: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder:text-slate-400"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Ville</label>
                <input
                  type="text"
                  value={orgSettings?.city || ''}
                  onChange={(e) => setOrgSettings({ ...orgSettings, city: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder:text-slate-400"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Email</label>
                <input
                  type="email"
                  value={orgSettings?.email || ''}
                  onChange={(e) => setOrgSettings({ ...orgSettings, email: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder:text-slate-400"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Téléphone</label>
                <input
                  type="tel"
                  inputMode="numeric"
                  maxLength={15}
                  value={orgSettings?.phone || ''}
                  onChange={(e) => setOrgSettings({ ...orgSettings, phone: e.target.value.replace(/\D/g, '').slice(0, 15) })}
                  placeholder="10 à 15 chiffres"
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder:text-slate-400"
                />
              </div>
            </div>
            <div className="border-t pt-4 mt-4">
              <h4 className="font-medium mb-3">Coordonnées bancaires</h4>
              <div>
                <label className="block text-sm font-medium mb-1">IBAN</label>
                <input
                  type="text"
                  maxLength={34}
                  value={orgSettings?.iban || ''}
                  onChange={(e) => setOrgSettings({ ...orgSettings, iban: e.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 34) })}
                  placeholder="FR7612345678901234567890123"
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder:text-slate-400"
                />
              </div>
              <div className="grid grid-cols-2 gap-4 mt-3">
                <div>
                  <label className="block text-sm font-medium mb-1">BIC</label>
                  <input
                    type="text"
                    maxLength={11}
                    value={orgSettings?.bic || ''}
                    onChange={(e) => setOrgSettings({ ...orgSettings, bic: e.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 11) })}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder:text-slate-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Banque</label>
                  <input
                    type="text"
                    value={orgSettings?.bank_name || ''}
                    onChange={(e) => setOrgSettings({ ...orgSettings, bank_name: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder:text-slate-400"
                  />
                </div>
              </div>
            </div>
            <div className="border-t pt-4 mt-4">
              <h4 className="font-medium mb-3">Numérotation des factures</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Préfixe</label>
                  <input
                    type="text"
                    value={orgSettings?.invoice_prefix || ''}
                    onChange={(e) => setOrgSettings({ ...orgSettings, invoice_prefix: e.target.value })}
                    placeholder="FAC"
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder:text-slate-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Prochain numéro</label>
                  <input
                    type="number"
                    value={orgSettings?.invoice_next_number || ''}
                    onChange={(e) => setOrgSettings({ ...orgSettings, invoice_next_number: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder:text-slate-400"
                  />
                </div>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                Format: {orgSettings?.invoice_prefix || 'FAC'}-2026-{String(orgSettings?.invoice_next_number || 1).padStart(5, '0')}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOrgSettingsModal(false)}>Annuler</Button>
            <Button onClick={handleUpdateOrgSettings}>Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Beneficiary Modal */}
      <Dialog open={showBeneficiaryModal} onOpenChange={setShowBeneficiaryModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Nouveau bénéficiaire</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">Nom *</label>
                <input
                  type="text"
                  value={beneficiaryForm.name}
                  onChange={(e) => setBeneficiaryForm({ ...beneficiaryForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder:text-slate-400"
                  data-testid="beneficiary-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Type *</label>
                <select
                  value={beneficiaryForm.beneficiary_type}
                  onChange={(e) => setBeneficiaryForm({ ...beneficiaryForm, beneficiary_type: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder:text-slate-400"
                  data-testid="beneficiary-type-select"
                >
                  <option value="opco">OPCO</option>
                  <option value="company">Entreprise</option>
                  <option value="student">Étudiant</option>
                  <option value="other">Autre</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">SIRET</label>
                <input
                  type="text"
                  value={beneficiaryForm.siret}
                  onChange={(e) => setBeneficiaryForm({ ...beneficiaryForm, siret: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder:text-slate-400"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">Email</label>
                <input
                  type="email"
                  value={beneficiaryForm.email}
                  onChange={(e) => setBeneficiaryForm({ ...beneficiaryForm, email: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder:text-slate-400"
                  data-testid="beneficiary-email-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Téléphone</label>
                <input
                  type="tel"
                  value={beneficiaryForm.phone}
                  onChange={(e) => setBeneficiaryForm({ ...beneficiaryForm, phone: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder:text-slate-400"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Contact</label>
                <input
                  type="text"
                  value={beneficiaryForm.contact_name}
                  onChange={(e) => setBeneficiaryForm({ ...beneficiaryForm, contact_name: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder:text-slate-400"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">Adresse</label>
                <input
                  type="text"
                  value={beneficiaryForm.address}
                  onChange={(e) => setBeneficiaryForm({ ...beneficiaryForm, address: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder:text-slate-400"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Code postal</label>
                <input
                  type="text"
                  value={beneficiaryForm.postal_code}
                  onChange={(e) => setBeneficiaryForm({ ...beneficiaryForm, postal_code: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder:text-slate-400"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Ville</label>
                <input
                  type="text"
                  value={beneficiaryForm.city}
                  onChange={(e) => setBeneficiaryForm({ ...beneficiaryForm, city: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder:text-slate-400"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBeneficiaryModal(false)}>Annuler</Button>
            <Button onClick={handleCreateBeneficiary} data-testid="create-beneficiary-btn">Créer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Funding Modal */}
      <Dialog open={showFundingModal} onOpenChange={setShowFundingModal}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Nouveau financement</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Étudiant *</label>
                <select
                  value={fundingForm.student_id}
                  onChange={(e) => setFundingForm({ ...fundingForm, student_id: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder:text-slate-400"
                  data-testid="funding-student-select"
                >
                  <option value="">Sélectionner...</option>
                  {students.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Montant total (€) *</label>
                <input
                  type="number"
                  value={fundingForm.total_amount}
                  onChange={(e) => setFundingForm({ ...fundingForm, total_amount: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder:text-slate-400"
                  data-testid="funding-amount-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">N° Dossier OPCO</label>
                <input
                  type="text"
                  value={fundingForm.dossier_number}
                  onChange={(e) => setFundingForm({ ...fundingForm, dossier_number: e.target.value })}
                  placeholder="ex: AKTO-2026-12345"
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder:text-slate-400"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Référence dossier</label>
                <input
                  type="text"
                  value={fundingForm.dossier_reference}
                  onChange={(e) => setFundingForm({ ...fundingForm, dossier_reference: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder:text-slate-400"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Début formation</label>
                <input
                  type="date"
                  value={fundingForm.training_start_date}
                  onChange={(e) => setFundingForm({ ...fundingForm, training_start_date: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder:text-slate-400"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Fin formation</label>
                <input
                  type="date"
                  value={fundingForm.training_end_date}
                  onChange={(e) => setFundingForm({ ...fundingForm, training_end_date: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder:text-slate-400"
                />
              </div>
            </div>

            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium">Répartition du financement</h4>
                <Button variant="outline" size="sm" onClick={addAllocation}>
                  <Plus className="w-4 h-4 mr-1" />
                  Ajouter
                </Button>
              </div>
              
              {fundingForm.allocations.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">
                  Ajoutez au moins un bénéficiaire (OPCO, entreprise ou étudiant)
                </p>
              ) : (
                <div className="space-y-4">
                  {fundingForm.allocations.map((alloc, index) => (
                    <div key={index} className="p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg space-y-3">
                      <div className="flex items-center gap-3">
                        <select
                          value={alloc.beneficiary_id}
                          onChange={(e) => updateAllocation(index, 'beneficiary_id', e.target.value)}
                          className="flex-1 px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm"
                        >
                          <option value="">Bénéficiaire...</option>
                          {beneficiaries.map((b) => (
                            <option key={b.id} value={b.id}>{b.name} ({b.beneficiary_type})</option>
                          ))}
                        </select>
                        <input
                          type="number"
                          placeholder="%"
                          value={alloc.percentage}
                          onChange={(e) => updateAllocation(index, 'percentage', e.target.value)}
                          className="w-16 px-2 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm"
                        />
                        <input
                          type="number"
                          placeholder="Montant €"
                          value={alloc.amount}
                          onChange={(e) => updateAllocation(index, 'amount', e.target.value)}
                          className="w-28 px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm"
                        />
                        <Button variant="ghost" size="sm" onClick={() => removeAllocation(index)}>
                          <X className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                      <div className="pl-1">
                        <p className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Dates d'échéance (optionnel)</p>
                        <div className="flex flex-wrap gap-2">
                          {(alloc.schedule_dates || ['']).map((d, di) => (
                            <div key={di} className="flex items-center gap-1">
                              <input
                                type="date"
                                value={d}
                                onChange={(e) => {
                                  const newAllocations = [...fundingForm.allocations];
                                  const dates = [...(newAllocations[index].schedule_dates || [''])];
                                  dates[di] = e.target.value;
                                  newAllocations[index].schedule_dates = dates;
                                  setFundingForm({ ...fundingForm, allocations: newAllocations });
                                }}
                                className="px-2 py-1 border border-slate-300 dark:border-slate-600 rounded text-xs"
                              />
                              {(alloc.schedule_dates || []).length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    const newAllocations = [...fundingForm.allocations];
                                    newAllocations[index].schedule_dates = newAllocations[index].schedule_dates.filter((_, i) => i !== di);
                                    setFundingForm({ ...fundingForm, allocations: newAllocations });
                                  }}
                                  className="text-red-400 hover:text-red-600 text-xs"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              )}
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={() => {
                              const newAllocations = [...fundingForm.allocations];
                              newAllocations[index].schedule_dates = [...(newAllocations[index].schedule_dates || ['']), ''];
                              setFundingForm({ ...fundingForm, allocations: newAllocations });
                            }}
                            className="px-2 py-1 text-xs text-violet-600 hover:text-violet-700 border border-dashed border-violet-300 rounded"
                          >
                            + Date
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFundingModal(false)}>Annuler</Button>
            <Button onClick={handleCreateFunding} data-testid="create-funding-btn">Créer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invoice Modal */}
      <Dialog open={showInvoiceModal} onOpenChange={setShowInvoiceModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Créer une facture</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium mb-1">Type de facture</label>
              <select
                value={invoiceForm.invoice_type}
                onChange={(e) => setInvoiceForm({ ...invoiceForm, invoice_type: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder:text-slate-400"
              >
                <option value="standard">Standard</option>
                <option value="opco">Style OPCO</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Allocation de financement *</label>
              <select
                value={invoiceForm.allocation_id}
                onChange={(e) => setInvoiceForm({ ...invoiceForm, allocation_id: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder:text-slate-400"
                data-testid="invoice-allocation-select"
              >
                <option value="">Sélectionner...</option>
                {(Array.isArray(fundings) ? fundings : []).flatMap(f => 
                  (f.allocations || []).map(a => (
                    <option key={a.id} value={a.id}>
                      {f.student_name} - {a.beneficiary_name} ({a.amount?.toLocaleString('fr-FR')} €)
                    </option>
                  ))
                )}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Description *</label>
              <textarea
                value={invoiceForm.description}
                onChange={(e) => setInvoiceForm({ ...invoiceForm, description: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder:text-slate-400"
                placeholder="Formation professionnelle - [Nom de la formation]"
                data-testid="invoice-description-input"
              />
            </div>
            {invoiceForm.invoice_type === 'opco' && (
              <div>
                <label className="block text-sm font-medium mb-1">N° de versement</label>
                <input
                  type="number"
                  value={invoiceForm.installment_number}
                  onChange={(e) => setInvoiceForm({ ...invoiceForm, installment_number: e.target.value })}
                  placeholder="1, 2, 3..."
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder:text-slate-400"
                />
              </div>
            )}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Quantité</label>
                <input
                  type="number"
                  value={invoiceForm.quantity}
                  onChange={(e) => setInvoiceForm({ ...invoiceForm, quantity: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder:text-slate-400"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Unité</label>
                <input
                  type="text"
                  value={invoiceForm.unit}
                  onChange={(e) => setInvoiceForm({ ...invoiceForm, unit: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder:text-slate-400"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Prix HT (€) *</label>
                <input
                  type="number"
                  value={invoiceForm.unit_price_ht}
                  onChange={(e) => setInvoiceForm({ ...invoiceForm, unit_price_ht: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder:text-slate-400"
                  data-testid="invoice-price-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">TVA (%)</label>
                <select
                  value={invoiceForm.vat_rate}
                  onChange={(e) => setInvoiceForm({ ...invoiceForm, vat_rate: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder:text-slate-400"
                >
                  <option value="0">0% (Exonéré)</option>
                  <option value="20">20%</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Échéance *</label>
                <input
                  type="date"
                  value={invoiceForm.due_date}
                  onChange={(e) => setInvoiceForm({ ...invoiceForm, due_date: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder:text-slate-400"
                  data-testid="invoice-duedate-input"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInvoiceModal(false)}>Annuler</Button>
            <Button onClick={handleCreateInvoice} data-testid="create-invoice-btn">Créer la facture</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Group Schedule Modal */}
      <Dialog open={showGroupScheduleModal} onOpenChange={setShowGroupScheduleModal}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Créer un échéancier de groupe</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Créez un échéancier qui sera automatiquement appliqué à tous les étudiants d'un cours ou groupe.
            </p>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Cours</label>
                <select
                  value={groupScheduleForm.course_id}
                  onChange={(e) => setGroupScheduleForm({ ...groupScheduleForm, course_id: e.target.value, group_id: '' })}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder:text-slate-400"
                >
                  <option value="">Sélectionner un cours...</option>
                  {courses.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">ou Groupe</label>
                <select
                  value={groupScheduleForm.group_id}
                  onChange={(e) => setGroupScheduleForm({ ...groupScheduleForm, group_id: e.target.value, course_id: '' })}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder:text-slate-400"
                >
                  <option value="">Sélectionner un groupe...</option>
                  {groups.map((g) => (
                    <option key={g.id} value={g.id}>{g.name}</option>
                  ))}
                </select>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Prix total de la formation (€) *</label>
              <input
                type="number"
                value={groupScheduleForm.total_amount}
                onChange={(e) => setGroupScheduleForm({ ...groupScheduleForm, total_amount: e.target.value })}
                placeholder="5000"
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder:text-slate-400"
              />
            </div>
            
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium">Versements</h4>
                <Button variant="outline" size="sm" onClick={addGroupInstallment}>
                  <Plus className="w-4 h-4 mr-1" />
                  Ajouter versement
                </Button>
              </div>
              
              <div className="space-y-3">
                {groupScheduleForm.installments.map((inst, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                    <input
                      type="date"
                      value={inst.due_date}
                      onChange={(e) => updateGroupInstallment(index, 'due_date', e.target.value)}
                      className="flex-1 px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                    />
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        value={inst.percentage}
                        onChange={(e) => updateGroupInstallment(index, 'percentage', e.target.value)}
                        className="w-20 px-2 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm text-center bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                      />
                      <span className="text-sm text-slate-500">%</span>
                    </div>
                    <span className="text-sm text-slate-600 w-28 text-right">
                      {groupScheduleForm.total_amount 
                        ? `${((parseFloat(groupScheduleForm.total_amount) * (inst.percentage || 0)) / 100).toLocaleString('fr-FR')} €`
                        : '-'
                      }
                    </span>
                    {groupScheduleForm.installments.length > 1 && (
                      <Button variant="ghost" size="sm" onClick={() => removeGroupInstallment(index)}>
                        <X className="w-4 h-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
              
              {/* Total percentage indicator */}
              <div className="mt-3 flex items-center justify-between p-2 bg-slate-100 dark:bg-slate-700 rounded-lg">
                <span className="text-sm font-medium">Total des pourcentages:</span>
                <span className={`text-sm font-bold ${
                  Math.abs(groupScheduleForm.installments.reduce((sum, inst) => sum + (parseFloat(inst.percentage) || 0), 0) - 100) < 0.1
                    ? 'text-green-600'
                    : 'text-red-600'
                }`}>
                  {groupScheduleForm.installments.reduce((sum, inst) => sum + (parseFloat(inst.percentage) || 0), 0).toFixed(1)}%
                </span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGroupScheduleModal(false)}>Annuler</Button>
            <Button onClick={handleCreateGroupSchedule}>Créer l'échéancier</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FinanceModulePage;
